import logger from "../../../../../../utils/logger";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../../../store/adminData/adminData";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { getBlob, ref } from "firebase/storage";
import { storage } from "../../../../../../services/firebaseService/connection";
import { updateEventNotifications } from "../../../../../../services/firebaseService/endPoints/admin/content/events";
import { postUpdateCDNUtil } from "../../../../../../helperFunctions/util";

import { normalizeNotificationTimestamps } from "../utils/helpers";
import {
  setFormErrorsFromZod,
  toApiFormat,
} from "../../../../../../schemas/event";
import {
  createEventService,
  updateEventService,
} from "../../../../../../services/firebaseService/endPoints/admin/content/events/form";
import useContentFormParams from "./useContentFormParams";
import { toast } from "react-toastify";

export const useFormSubmit = ({
  getValues,
  contentId,
  setIsLoading,
  editConfiguration,
  oldNotificationInUtc,
  additionalLocations,
  setError,
  matchList,
  matchDate,
  isParent = false,
  selectedGroups = null,
  clubId: parentClubId = null,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const clubId = parentClubId || groupData?.clubId;
  const currentGroup = groupData?.id;
  const { formMode } = useContentFormParams();

  const validateData = () => {
    try {
      const formValues = getValues();

      // Use current group ID for both parent and regular groups
      const targetGroupId = currentGroup;

      const result = toApiFormat(formValues, {
        mode: formMode,
        matchDate,
        additionalLocations,
        matchList,
        groupId: targetGroupId,
        clubId,
      });

      // Add audience field for parent groups
      if (isParent && selectedGroups && result.success) {
        result.data.audience = {
          selectionType: selectedGroups.selectionType || "all",
          selectedValues: selectedGroups.selectedValues || null,
          groupIds: selectedGroups.groupIds || null,
        };
      }

      return result;
    } catch (error) {
      logger.error("Failed to validate data:", error);

      return {
        success: false,
        error: {
          message: "Unexpected validation error. Please contact support.",
          originalError: error,
        },
      };
    }
  };

  const handleValidationError = (result) => {
    logger.error("Validation failed:", result.error);

    if (result.error.name === "ZodError") {
      setFormErrorsFromZod(result.error, setError);
      let errorMessage = "Error in event creation. Please try again later.";

      setError("root", {
        type: "validation",
        message: errorMessage,
      });
    } else if (result.error.name === "LocationError") {
      // Handle location-specific errors
      if (result.fieldErrors?.additionalLocations) {
        setError("location", {
          type: "manual",
          message: result.fieldErrors.additionalLocations[0],
        });
      }
      setError("root", {
        type: "manual",
        message: result.error.message,
      });
    } else {
      setError("root", {
        type: "manual",
        message:
          result.error.message ||
          "Unable to submit the form. Please check all fields and try again.",
      });
    }

    window.scrollTo(0, 0);
  };

  const handleCreateEvent = async () => {
    setIsLoading(true);

    const result = validateData();

    if (!result.success) {
      handleValidationError(result);
      setIsLoading(false);
      return;
    }

    const contentData = result.data;
    const { imageFile } = result.metadata;

    // Use current group ID directly for both parent and regular groups
    const targetGroupId = currentGroup;
    const targetPath = isParent ? "/clubadmin/content" : "/admin/content";
    const targetQueryKey = isParent
      ? ["parent-events", targetGroupId]
      : ["events", targetGroupId];

    try {
      const content = await createEventService(contentData, {
        imageFile: imageFile,
        isParentPost: isParent,
        customGroupId: targetGroupId,
      });

      const notificationTimezone =
        typeof Intl !== "undefined"
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : undefined;
      await updateEventNotifications(
        contentData,
        normalizeNotificationTimestamps(oldNotificationInUtc),
        targetGroupId,
        content.id,
        false,
        notificationTimezone,
      );

      postUpdateCDNUtil(targetGroupId, "event");

      queryClient.invalidateQueries({ queryKey: targetQueryKey });

      toast.success("Event created successfully");

      navigate(targetPath, { replace: true });
    } catch (error) {
      logger.error("Failed to create event:", error);
      toast.error("Failed to create event. Please try again.");
      setError("root", {
        type: "manual",
        message: error.message || "Failed to create event. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditEvent = async () => {
    setIsLoading(true);

    const result = validateData();

    if (!result.success) {
      handleValidationError(result);
      setIsLoading(false);
      return;
    }

    const contentData = result.data;
    const { imageFile } = result.metadata;

    // Use current group ID directly for both parent and regular groups
    const targetGroupId = currentGroup;
    const targetPath = isParent ? "/clubadmin/content" : "/admin/content";
    const targetQueryKey = isParent
      ? ["parent-events", targetGroupId]
      : ["events", targetGroupId];

    try {
      await updateEventService(contentData, contentId, {
        imageFile: editConfiguration.imageChanged ? imageFile : undefined,
        deleteImage: editConfiguration.imageChanged && !imageFile,
        isParent: isParent,
        customGroupId: targetGroupId,
      });

      const notificationTimezone =
        typeof Intl !== "undefined"
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : undefined;
      await updateEventNotifications(
        contentData,
        normalizeNotificationTimestamps(oldNotificationInUtc),
        targetGroupId,
        contentId,
        false,
        notificationTimezone,
      );

      postUpdateCDNUtil(targetGroupId, "event");

      queryClient.invalidateQueries({ queryKey: targetQueryKey });

      toast.success("Event updated successfully");

      navigate(targetPath, { replace: true });
    } catch (error) {
      logger.error("Failed to update event:", error);
      toast.error("Failed to update event. Please try again.");
      setError("root", {
        type: "manual",
        message: error.message || "Failed to update event. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyEvent = async () => {
    setIsLoading(true);

    const result = validateData();

    if (!result.success) {
      handleValidationError(result);
      setIsLoading(false);
      return;
    }

    const contentData = result.data;
    const { imageFile } = result.metadata;

    try {
      let imageBlob = undefined;

      // Handle image copying:
      // - If imageFile is a File object (user uploaded new image), use it directly
      // - If imageFile is a string URL (original image from API), download from Firebase Storage
      // - If imageFile is null/undefined, no image
      if (imageFile) {
        if (imageFile instanceof File || imageFile instanceof Blob) {
          // User uploaded a new image, use it directly
          imageBlob = imageFile;
        } else if (typeof imageFile === "string" && imageFile.trim() !== "") {
          // Original event had an image (URL string), download it from Firebase Storage
          // Images are stored at {groupId}/content/images/{contentId} regardless of parent/regular
          const sourceGroupId = currentGroup;
          const storageRef = ref(storage);
          const imagePath = "content/images/";
          const folderRef = ref(
            storageRef,
            `${sourceGroupId}/${imagePath}${contentId}`,
          );

          try {
            imageBlob = await getBlob(folderRef);
          } catch (err) {
            logger.error("Failed to copy image:", err);
            // Continue without image if download fails
          }
        }
      }

      const targetGroupId = currentGroup;
      const targetPath = isParent ? "/clubadmin/content" : "/admin/content";
      const targetQueryKey = isParent
        ? ["parent-events", targetGroupId]
        : ["events", targetGroupId];

      const content = await createEventService(contentData, {
        imageFile: imageBlob,
        isParentPost: isParent,
        customGroupId: targetGroupId,
      });

      const notificationTimezone =
        typeof Intl !== "undefined"
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : undefined;
      // Clear old notifications when copying, don't copy old notifications
      await updateEventNotifications(
        contentData,
        [null],
        targetGroupId,
        content.id,
        false,
        notificationTimezone,
      );

      postUpdateCDNUtil(targetGroupId, "event");

      queryClient.invalidateQueries({ queryKey: targetQueryKey });

      toast.success("Event created successfully");

      navigate(targetPath, { replace: true });
    } catch (error) {
      logger.error("Failed to copy event:", error);
      toast.error("Failed to copy event. Please try again.");
      setError("root", {
        type: "manual",
        message: error.message || "Failed to copy event. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (mode) => {
    if (mode === "copy") {
      await handleCopyEvent();
    } else if (mode === "edit") {
      await handleEditEvent();
    } else if (mode === "create") {
      await handleCreateEvent();
    }
  };

  return { handleSubmit };
};
